import { useEffect, useState } from 'react'
import { cn } from '../lib/cn'
import { getStaffSession } from '../lib/staff'

/**
 * An image behind an authenticated route.
 *
 * A plain `<img src>` cannot carry an `Authorization` header, so every
 * students' photograph rendered straight into one came back 401. This fetches
 * the bytes with the staff token and renders them from an object URL instead.
 *
 * The alternative was a signed token in the query string, which would have
 * created a second way to reach these files — one that works outside the
 * console, survives in a browser history, and is pasteable into a chat. These
 * are photographs of named students; one authenticated path is enough.
 *
 * Object URLs are revoked on unmount, and the fetch is abandoned if the
 * component goes away first — a Committee member scrolling a list of 1,250
 * replies should not leave a few hundred blobs pinned in memory behind them.
 */
export function AuthImage({
  src,
  alt = '',
  className,
}: {
  src: string
  alt?: string
  className?: string
}) {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let revoked: string | null = null
    const abort = new AbortController()

    setUrl(null)
    setFailed(false)

    fetch(src, {
      headers: { Authorization: `Bearer ${getStaffSession() ?? ''}` },
      signal: abort.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(String(res.status))
        const blob = await res.blob()
        revoked = URL.createObjectURL(blob)
        setUrl(revoked)
      })
      .catch((e) => {
        /* An abort is this component going away, not a failure to show. */
        if ((e as Error).name !== 'AbortError') setFailed(true)
      })

    return () => {
      abort.abort()
      if (revoked) URL.revokeObjectURL(revoked)
    }
  }, [src])

  if (failed) {
    return (
      <span
        className={cn('grid place-items-center bg-edge-soft text-[10px] text-ink-400', className)}
      >
        no image
      </span>
    )
  }

  if (!url) {
    return <span className={cn('block animate-pulse bg-edge-soft', className)} aria-hidden />
  }

  return <img src={url} alt={alt} className={className} />
}
